import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Row, Spinner } from "reactstrap";
import {
  IoPeopleOutline,
  IoCheckmarkDoneOutline,
  IoTimeOutline,
  IoCashOutline,
  IoAlertCircleOutline,
  IoCalendarOutline,
  IoDocumentTextOutline,
  IoIdCardOutline,
  IoPersonAddOutline,
  IoGiftOutline,
  IoRibbonOutline,
  IoAirplaneOutline,
} from "react-icons/io5";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import StatCard from "../../../../components/Cards/StatCard";
import sidebar from "../../utils/sidebar";
import { get } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { printLeaveType } from "../../utils/helpers";
import { formatMoney } from "../../utils/payrollStatus";
import ViewLeaveRequestDrawer from "../LeaveRequests/Drawers/View";
import "./styles.css";

const TODAY_COLORS = {
  present: "#20c997",
  absent: "#fa5252",
  on_leave: "#4dabf7",
  not_recorded: "#dee2e6",
};

const DEPARTMENT_COLORS = [
  "#6C63FF", "#20c997", "#f76707", "#4dabf7", "#fa5252",
  "#ffd43b", "#ae3ec9", "#2f9e44", "#e64980", "#0c8599",
];

const tooltipStyle = {
  fontSize: "0.78rem",
  borderRadius: 8,
  border: "1px solid #eee",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

function initials(name) {
  if (!name) return "—";
  return name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
}

function EmptyState({ icon, message }) {
  return (
    <div className="hr-empty-state">
      {icon}
      <span className="mt-2">{message}</span>
    </div>
  );
}

/** Colour an upcoming expiry by how soon it bites. */
function DaysLeft({ days, t }) {
  const tone = days <= 14 ? "hr-days-urgent" : days <= 30 ? "hr-days-soon" : "hr-days-ok";
  return <span className={`hr-days-left ${tone}`}>{t('hr.home.inDays', { count: days })}</span>;
}

function AttentionGroup({ icon, label, count, children }) {
  if (!count) return null;
  return (
    <div className="hr-attention-group">
      <div className="hr-attention-heading">
        {icon}
        <span>{label}</span>
        <span className="hr-attention-count">{count}</span>
      </div>
      {children}
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatDate, settings } = useSettings();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeaveRequestId, setSelectedLeaveRequestId] = useState(null);

  const currency = settings?.sales?.base_currency || "";

  const getHomeData = async () => {
    setLoading(true);
    const response = await get('/hr/home');
    if (response?.success) {
      setData(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    getHomeData();
  }, []);

  const attendance = data?.today_attendance ?? {};
  const attendanceSlices = ['present', 'absent', 'on_leave', 'not_recorded']
    .map(key => ({ key, name: t(`hr.home.${key === 'on_leave' ? 'onLeave' : key === 'not_recorded' ? 'notRecorded' : key}`), value: attendance[key] ?? 0 }))
    .filter(slice => slice.value > 0);

  const attentionTotal =
    (data?.pending_leave_requests_count ?? 0) +
    (data?.draft_payroll_runs?.length ?? 0) +
    (data?.expiring_contracts?.length ?? 0) +
    (data?.expiring_ids?.length ?? 0);

  const departments = (data?.headcount_by_department ?? []).map(row => ({
    ...row,
    department: row.department === 'Unassigned' ? t('hr.home.unassigned') : row.department,
  }));

  const hasPayrollHistory = (data?.payroll_cost_trend ?? []).some(month => month.total_net > 0);

  const QUICK_ACTIONS = [
    { icon: <IoPersonAddOutline size={16} />, label: t('hr.home.addEmployee'), to: '/hr/employees/create' },
    { icon: <IoTimeOutline size={16} />, label: t('hr.home.recordAttendance'), to: '/hr/attendance/bulk-create' },
    { icon: <IoAirplaneOutline size={16} />, label: t('hr.home.newLeaveRequest'), to: '/hr/leave-requests/create' },
    { icon: <IoCashOutline size={16} />, label: t('hr.home.runPayroll'), to: '/hr/payroll/create' },
  ];

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('hr.home.title')} onReload={getHomeData} />

        {loading || !data ? (
          <Spinner color="primary" className="d-block mx-auto my-4" />
        ) : (
          <div className="subheader-page-content scrollable">
            {/* ── KPI row ── */}
            <Row className="g-3 mb-4">
              <Col md="3">
                <StatCard
                  icon={<IoPeopleOutline size={18} color="#6C63FF" />}
                  iconBg="#f0eeff"
                  value={data.headcount}
                  label={`${t('hr.home.headcount')} · ${t('hr.home.newThisMonth', { count: data.new_hires_this_month })}`}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoCheckmarkDoneOutline size={18} color="#20c997" />}
                  iconBg="#e6faf5"
                  value={attendance.present ?? 0}
                  label={t('hr.home.inToday')}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoAlertCircleOutline size={18} color="#f76707" />}
                  iconBg="#fff4e6"
                  value={attentionTotal}
                  label={t('hr.home.needsAttention')}
                />
              </Col>
              <Col md="3">
                <StatCard
                  icon={<IoCashOutline size={18} color="#4dabf7" />}
                  iconBg="#e8f4ff"
                  value={formatMoney(data.committed_monthly_cost, currency)}
                  label={t('hr.home.monthlyCost')}
                />
              </Col>
            </Row>

            {/* ── Needs attention + Today ── */}
            <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="7" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('hr.home.needsAttention')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="hr-panel-scroll">
                    {attentionTotal === 0 ? (
                      <EmptyState
                        icon={<IoCheckmarkDoneOutline size={32} style={{ opacity: 0.4 }} />}
                        message={t('hr.home.allClear')}
                      />
                    ) : (
                      <>
                        <AttentionGroup
                          icon={<IoCalendarOutline size={14} />}
                          label={t('hr.home.leaveToApprove')}
                          count={data.pending_leave_requests_count}
                        >
                          {data.pending_leave_requests.map(request => (
                            <div
                              key={request.id}
                              className="hr-attention-item"
                              onClick={() => setSelectedLeaveRequestId(request.id)}
                            >
                              <div className="hr-avatar">{initials(request.employee_name)}</div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="hr-attention-name text-truncate">{request.employee_name}</div>
                                <div className="hr-attention-meta">
                                  {printLeaveType(request.leave_type)} · {formatDate(request.start_date)} → {formatDate(request.end_date)}
                                </div>
                              </div>
                              <span className="hr-attention-meta">{request.total_days}d</span>
                            </div>
                          ))}
                        </AttentionGroup>

                        <AttentionGroup
                          icon={<IoDocumentTextOutline size={14} />}
                          label={t('hr.home.draftPayroll')}
                          count={data.draft_payroll_runs.length}
                        >
                          {data.draft_payroll_runs.map(run => (
                            <div
                              key={run.id}
                              className="hr-attention-item"
                              onClick={() => navigate(`/hr/payroll/view/${run.id}`)}
                            >
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="hr-attention-name text-truncate">{run.name || run.reference}</div>
                                <div className="hr-attention-meta">
                                  {formatDate(run.period_start)} → {formatDate(run.period_end)} · {run.employees_count}
                                </div>
                              </div>
                              <span className="fw-semibold" style={{ fontSize: "0.83rem" }}>
                                {formatMoney(run.total_net, currency)}
                              </span>
                            </div>
                          ))}
                        </AttentionGroup>

                        <AttentionGroup
                          icon={<IoDocumentTextOutline size={14} />}
                          label={t('hr.home.contractsExpiring')}
                          count={data.expiring_contracts.length}
                        >
                          {data.expiring_contracts.map(employee => (
                            <div
                              key={employee.id}
                              className="hr-attention-item"
                              onClick={() => navigate(`/hr/employees/view/${employee.id}`)}
                            >
                              <div className="hr-avatar">{initials(employee.employee_name)}</div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="hr-attention-name text-truncate">{employee.employee_name}</div>
                                <div className="hr-attention-meta">
                                  {employee.job_title || '—'} · {formatDate(employee.expires_on)}
                                </div>
                              </div>
                              <DaysLeft days={employee.days_left} t={t} />
                            </div>
                          ))}
                        </AttentionGroup>

                        <AttentionGroup
                          icon={<IoIdCardOutline size={14} />}
                          label={t('hr.home.idsExpiring')}
                          count={data.expiring_ids.length}
                        >
                          {data.expiring_ids.map(employee => (
                            <div
                              key={employee.id}
                              className="hr-attention-item"
                              onClick={() => navigate(`/hr/employees/view/${employee.id}`)}
                            >
                              <div className="hr-avatar">{initials(employee.employee_name)}</div>
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="hr-attention-name text-truncate">{employee.employee_name}</div>
                                <div className="hr-attention-meta">
                                  {employee.id_type || '—'} · {formatDate(employee.expires_on)}
                                </div>
                              </div>
                              <DaysLeft days={employee.days_left} t={t} />
                            </div>
                          ))}
                        </AttentionGroup>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="5" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('hr.home.today')}</h6>
                <Card className="flex-grow-1">
                  <CardBody>
                    {attendanceSlices.length === 0 ? (
                      <EmptyState
                        icon={<IoTimeOutline size={32} style={{ opacity: 0.4 }} />}
                        message={t('hr.home.noAttendanceToday')}
                      />
                    ) : (
                      <div className="d-flex align-items-center gap-4">
                        <PieChart width={130} height={130}>
                          <Pie
                            data={attendanceSlices}
                            dataKey="value"
                            nameKey="name"
                            cx={65}
                            cy={65}
                            innerRadius={30}
                            outerRadius={52}
                            paddingAngle={2}
                          >
                            {attendanceSlices.map(slice => (
                              <Cell key={slice.key} fill={TODAY_COLORS[slice.key]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={tooltipStyle} />
                        </PieChart>
                        <div className="hr-today-legend flex-grow-1">
                          {attendanceSlices.map(slice => (
                            <div key={slice.key} className="hr-today-legend-item">
                              <div className="hr-today-dot" style={{ background: TODAY_COLORS[slice.key] }} />
                              <span className="flex-grow-1">{slice.name}</span>
                              <span className="fw-semibold">{slice.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <hr className="my-3" />

                    <div className="hr-attention-heading">
                      <IoAirplaneOutline size={14} />
                      <span>{t('hr.home.whoIsOut')}</span>
                    </div>
                    {data.on_leave_today.length === 0 ? (
                      <p className="text-muted small mb-0">{t('hr.home.nobodyOut')}</p>
                    ) : (
                      <div className="hr-out-list">
                        {data.on_leave_today.map(leave => (
                          <div key={leave.id} className="hr-out-item">
                            <div className="hr-avatar">{initials(leave.employee_name)}</div>
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="fw-semibold text-truncate">{leave.employee_name}</div>
                              <div className="hr-attention-meta">{printLeaveType(leave.leave_type)}</div>
                            </div>
                            <span className="hr-attention-meta">
                              {t('hr.home.returns', { date: formatDate(leave.returns_on) })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ── Composition + payroll trend ── */}
            <Row className="g-3 mb-4" style={{ alignItems: "stretch" }}>
              <Col lg="5" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('hr.home.byDepartment')}</h6>
                <Card className="flex-grow-1">
                  <CardBody>
                    <div style={{ minHeight: 220 }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={departments} layout="vertical" margin={{ left: 10, right: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 11, fill: "#555" }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <YAxis
                            type="category"
                            dataKey="department"
                            tick={{ fontSize: 11, fill: "#555" }}
                            axisLine={false}
                            tickLine={false}
                            width={100}
                          />
                          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#f5f3ff" }} />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={22}>
                            {departments.map((_, i) => (
                              <Cell key={i} fill={DEPARTMENT_COLORS[i % DEPARTMENT_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col lg="7" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('hr.home.payrollTrend')}</h6>
                <Card className="flex-grow-1">
                  <CardBody>
                    {!hasPayrollHistory ? (
                      <EmptyState
                        icon={<IoCashOutline size={32} style={{ opacity: 0.4 }} />}
                        message={t('hr.home.noPayrollYet')}
                      />
                    ) : (
                      <div style={{ minHeight: 220 }}>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={data.payroll_cost_trend} margin={{ right: 8, left: -20 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#555" }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: "#555" }} axisLine={false} tickLine={false} width={70} />
                            <Tooltip
                              contentStyle={tooltipStyle}
                              cursor={{ fill: "#f5f3ff" }}
                              formatter={(value) => formatMoney(value, currency)}
                            />
                            <Bar dataKey="total_net" fill="#6C63FF" radius={[4, 4, 0, 0]} maxBarSize={40} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ── People moments + quick actions ── */}
            <Row className="g-3">
              <Col lg="4" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">
                  <IoGiftOutline className="me-1" />
                  {t('hr.home.birthdays')}
                </h6>
                <Card className="flex-grow-1">
                  <CardBody>
                    {data.birthdays_this_month.length === 0 ? (
                      <p className="text-muted small mb-0">{t('hr.home.noBirthdays')}</p>
                    ) : (
                      data.birthdays_this_month.map(person => (
                        <div key={person.id} className="hr-moment-item">
                          <div className="hr-avatar">{initials(person.employee_name)}</div>
                          <span className="flex-grow-1 text-truncate">{person.employee_name}</span>
                          <span className="hr-attention-meta">{formatDate(person.date)}</span>
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="4" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">
                  <IoRibbonOutline className="me-1" />
                  {t('hr.home.anniversaries')}
                </h6>
                <Card className="flex-grow-1">
                  <CardBody>
                    {data.anniversaries_this_month.length === 0 ? (
                      <p className="text-muted small mb-0">{t('hr.home.noAnniversaries')}</p>
                    ) : (
                      data.anniversaries_this_month.map(person => (
                        <div key={person.id} className="hr-moment-item">
                          <div className="hr-avatar">{initials(person.employee_name)}</div>
                          <span className="flex-grow-1 text-truncate">{person.employee_name}</span>
                          <span className="hr-attention-meta">{t('hr.home.years', { count: person.years })}</span>
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="4" className="d-flex flex-column">
                <h6 className="fw-bold mb-2">{t('hr.home.quickActions')}</h6>
                <Card className="flex-grow-1">
                  <CardBody className="d-flex flex-column gap-2">
                    {QUICK_ACTIONS.map(action => (
                      <button
                        key={action.to}
                        type="button"
                        className="hr-quick-action-btn"
                        onClick={() => navigate(action.to)}
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    ))}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </div>

      <ViewLeaveRequestDrawer
        leaveRequestId={selectedLeaveRequestId}
        isOpen={selectedLeaveRequestId !== null}
        onClose={() => setSelectedLeaveRequestId(null)}
        refreshLeaveRequests={getHomeData}
      />
    </SidebarLayout>
  );
}
